<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import ToggleSwitch from '@/js/components/ToggleSwitch.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import {
        KeyIndicateSyncDataType,
        getKeyIndicateSyncDataTypeLabel as label,
        isValidDaysBack,
        MAX_DAYS_BACK,
        type KeyIndicateSyncDataTypeInfo,
        type KeyIndicateSyncSetting,
    } from '@/js/services/api/backOffice/keyIndicateSyncSettingsApi';

    const props = defineProps<{
        dataTypes: KeyIndicateSyncDataTypeInfo[];
        idPrefix: string;
    }>();

    const settings = defineModel<KeyIndicateSyncSetting[]>({ required: true });

    const rows = computed(() =>
        props.dataTypes.flatMap((info) => {
            const setting = settings.value.find((s) => s.dataType === info.dataType);
            return setting ? [{ info, setting }] : [];
        })
    );

    const isTranscriptionsEnabled = computed(
        () =>
            settings.value.find((s) => s.dataType === KeyIndicateSyncDataType.Transcriptions)
                ?.isEnabled ?? false
    );

    // Trackers are only fetched together with transcriptions.
    const requiresTranscriptions = (dataType: KeyIndicateSyncDataType) =>
        dataType === KeyIndicateSyncDataType.TranscriptionSegmentTrackers;

    // Only blocks turning trackers on, so trackers stored as on without transcriptions can still be turned off.
    const isLocked = (dataType: KeyIndicateSyncDataType, isEnabled: boolean) =>
        requiresTranscriptions(dataType) && !isTranscriptionsEnabled.value && !isEnabled;

    // The agent and coaching form queries have no date filter, and trackers use the transcriptions' range.
    const typesWithoutDaysBack = [
        KeyIndicateSyncDataType.Agents,
        KeyIndicateSyncDataType.TranscriptionSegmentTrackers,
        KeyIndicateSyncDataType.CoachingForm,
    ];
    const usesDaysBack = (dataType: KeyIndicateSyncDataType) =>
        !typesWithoutDaysBack.includes(dataType);

    const update = (dataType: KeyIndicateSyncDataType, change: Partial<KeyIndicateSyncSetting>) => {
        const turnsOffTranscriptions =
            dataType === KeyIndicateSyncDataType.Transcriptions && change.isEnabled === false;

        settings.value = settings.value.map((s) => {
            if (s.dataType === dataType) {
                return { ...s, ...change };
            }
            if (turnsOffTranscriptions && requiresTranscriptions(s.dataType)) {
                return { ...s, isEnabled: false };
            }
            return s;
        });
    };
</script>

<template>
    <fieldset v-if="rows.length > 0" class="rounded-md border border-gray-light px-4 pb-4 pt-2">
        <legend class="text-overline px-1">
            {{ __('back-office-sync-data-types') }}
        </legend>
        <div class="flex flex-col gap-2">
            <div class="flex gap-4 text-caption-bold">
                <span class="flex-1">{{ __('back-office-sync-data-type-column-data-type') }}</span>
                <span class="w-24">{{ __('back-office-sync-data-type-column-synced') }}</span>
                <span class="w-24">{{ __('back-office-sync-data-type-days-back') }}</span>
            </div>
            <div
                v-for="{ info, setting } in rows"
                :key="info.dataType"
                class="flex items-center gap-4"
            >
                <div class="flex-1">
                    <span class="text-body">{{ label(info) }}</span>
                    <p
                        v-if="requiresTranscriptions(info.dataType) && !isTranscriptionsEnabled"
                        class="text-caption text-gray-medium"
                    >
                        {{ __('back-office-sync-data-type-requires-transcriptions') }}
                    </p>
                    <p v-if="!info.isEnabled" class="text-caption text-gray-medium">
                        {{ __('back-office-sync-data-type-disabled-globally') }}
                    </p>
                </div>
                <div class="w-24">
                    <ToggleSwitch
                        :model-value="setting.isEnabled"
                        :disabled="isLocked(info.dataType, setting.isEnabled)"
                        :aria-label="label(info)"
                        @update:model-value="update(info.dataType, { isEnabled: $event ?? false })"
                    />
                </div>
                <div class="w-24">
                    <TextInput
                        v-if="usesDaysBack(info.dataType)"
                        :id="`${idPrefix}-days-back-${info.dataType}`"
                        :model-value="setting.daysBack"
                        type="number"
                        min="1"
                        :max="MAX_DAYS_BACK"
                        :width="TextInputWidth.Tiny"
                        :invalid="!isValidDaysBack(setting.daysBack)"
                        :aria-label="`${label(info)}, ${__('back-office-sync-data-type-days-back')}`"
                        @update:model-value="update(info.dataType, { daysBack: Number($event) })"
                    />
                </div>
            </div>
        </div>
    </fieldset>
</template>
